import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  hideMindMapBranch,
  listModeratedBranches,
  restoreMindMapBranch,
} from '../../utils/mindmap/apiClient';
import {
  parseNodeBranchModerationChangedEvent,
} from '../../utils/mindmap/nodeBranchModeration';

function cacheKey(mindmapId) {
  return `mindmappi:branch-moderation:v1:${mindmapId}`;
}

function readCachedBranches(mindmapId) {
  if (!mindmapId || typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(cacheKey(mindmapId)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCachedBranches(mindmapId, branches) {
  if (!mindmapId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(cacheKey(mindmapId), JSON.stringify(branches));
  } catch {
    // A live in-memory projection still works when browser storage is unavailable.
  }
}

export function useNodeBranchModeration({ mindmapId, enabled, provider }) {
  const [branches, setBranches] = useState(() => readCachedBranches(mindmapId));
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setBranches(readCachedBranches(mindmapId));
    setError(null);
  }, [mindmapId]);

  const refresh = useCallback(async () => {
    if (!enabled || !mindmapId) {
      setBranches([]);
      return [];
    }
    try {
      const payload = await listModeratedBranches(mindmapId);
      const next = Array.isArray(payload?.branches) ? payload.branches : [];
      setBranches(next);
      writeCachedBranches(mindmapId, next);
      setError(null);
      return next;
    } catch (nextError) {
      setError(nextError);
      return null;
    }
  }, [enabled, mindmapId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const refreshOnFocus = () => void refresh();
    window.addEventListener('focus', refreshOnFocus);
    window.addEventListener('online', refreshOnFocus);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      window.removeEventListener('online', refreshOnFocus);
    };
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || !provider?.on || !provider?.off) return undefined;
    let timer = null;
    const scheduleRefresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void refresh(), 100);
    };
    const onStateless = ({ payload } = {}) => {
      if (parseNodeBranchModerationChangedEvent(payload, mindmapId)) scheduleRefresh();
    };
    const onStatus = ({ status } = {}) => {
      if (status === 'connected') scheduleRefresh();
    };
    provider.on('stateless', onStateless);
    provider.on('status', onStatus);
    return () => {
      if (timer) clearTimeout(timer);
      provider.off('stateless', onStateless);
      provider.off('status', onStatus);
    };
  }, [enabled, mindmapId, provider, refresh]);

  const hide = useCallback(async (nodeId, creatorUserId) => {
    if (!enabled || !mindmapId || isUpdating) return false;
    setIsUpdating(true);
    setError(null);
    try {
      await hideMindMapBranch(mindmapId, nodeId, creatorUserId);
      await refresh();
      return true;
    } catch (nextError) {
      setError(nextError);
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [enabled, isUpdating, mindmapId, refresh]);

  const restore = useCallback(async (nodeId) => {
    if (!enabled || !mindmapId || isUpdating) return false;
    setIsUpdating(true);
    setError(null);
    try {
      await restoreMindMapBranch(mindmapId, nodeId);
      await refresh();
      return true;
    } catch (nextError) {
      setError(nextError);
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [enabled, isUpdating, mindmapId, refresh]);

  return useMemo(() => ({
    branches,
    branchByNodeId: new Map(branches.map((branch) => [branch.nodeId, branch])),
    isUpdating,
    error,
    hide,
    restore,
    refresh,
  }), [branches, error, hide, isUpdating, refresh, restore]);
}
